import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Header from '../Header';
import Footer from '../Footer';
import { getIncidentBySlug, getOrgById } from './libraryData';
import { useAdmin } from '../../contexts/AdminContext';
import EditableText from '../admin/EditableText';
import SourcesEditor from '../admin/SourcesEditor';
import TimelineEditor from '../admin/TimelineEditor';
import './Library.css';

function slugify(text) {
  return text.toLowerCase().replace(/['']/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}

export default function IncidentDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { isAdmin } = useAdmin();
  const [apiData, setApiData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editedData, setEditedData] = useState({});
  const [showSourcesEditor, setShowSourcesEditor] = useState(false);
  const [showTimelineEditor, setShowTimelineEditor] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      try {
        const res = await fetch(`/api/incidents/${slug}`);
        if (res.ok) {
          const data = await res.json();
          // Parse JSON string fields
          if (typeof data.sources === 'string') data.sources = JSON.parse(data.sources);
          if (typeof data.updates === 'string') data.updates = JSON.parse(data.updates);
          if (typeof data.relatedOrgs === 'string') data.relatedOrgs = JSON.parse(data.relatedOrgs);
          if (typeof data.relatedMaterials === 'string') data.relatedMaterials = JSON.parse(data.relatedMaterials);
          if (typeof data.pressuredBy === 'string') data.pressuredBy = JSON.parse(data.pressuredBy);
          if (!cancelled) setApiData(data);
        }
      } catch (e) {
        console.error('Failed to fetch from API:', e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchData();
    return () => { cancelled = true; };
  }, [slug]);

  // Use API data if available, fall back to static
  const staticIncident = getIncidentBySlug(slug);
  const incident = apiData || staticIncident;

  const saveToAPI = async (field, value) => {
    try {
      const response = await fetch(`/api/incidents/${incident.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ [field]: value }),
      });
      if (!response.ok) {
        const err = await response.json();
        console.error('Save failed:', err);
        alert('Save failed: ' + (err.error || 'Unknown error'));
        return false;
      }
      return true;
    } catch (error) {
      console.error('Save error:', error);
      alert('Save failed: network error');
      return false;
    }
  };

  const handleFieldSave = async (field, value) => {
    if (field === 'title') {
      const newSlug = slugify(value);
      try {
        const response = await fetch(`/api/incidents/${incident.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ title: value, slug: newSlug }),
        });
        if (response.ok) {
          setEditedData(prev => ({ ...prev, title: value, slug: newSlug }));
          if (newSlug !== slug) navigate(`/incidents/${newSlug}`, { replace: true });
        } else {
          const err = await response.json();
          alert('Save failed: ' + (err.error || 'Unknown error'));
        }
      } catch (error) {
        alert('Save failed: network error');
      }
      return;
    }
    const ok = await saveToAPI(field, value);
    if (ok) setEditedData(prev => ({ ...prev, [field]: value }));
  };

  const handleSourcesSave = async (sources) => {
    const ok = await saveToAPI('sources', sources);
    if (ok) setEditedData(prev => ({ ...prev, sources }));
    setShowSourcesEditor(false);
  };

  const handleTimelineSave = async (updates) => {
    const ok = await saveToAPI('updates', updates);
    if (ok) setEditedData(prev => ({ ...prev, updates }));
    setShowTimelineEditor(false);
  };

  if (loading) {
    return (
      <>
        <Header />
        <main className='detail-page-container'>
          <div className='detail-content'>
            <p>Loading...</p>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  if (!incident) {
    return (
      <>
        <Header />
        <main className='detail-page-container'>
          <div className='detail-content'>
            <h1>Incident not found</h1>
            <p>The incident you are looking for does not exist.</p>
            <Link to='/library'>Back to Library</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const parts = dateStr.split('-');
    if (parts.length === 1) return dateStr; // Just a year
    const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    const year = parts[0];
    const month = months[parseInt(parts[1], 10) - 1];
    if (parts.length === 2) return `${month} ${year}`;
    const day = parseInt(parts[2], 10);
    return `${day} ${month} ${year}`;
  };

  const pageTitle = `${incident.title} - Incidents - Hasbara Tracker`;
  const pageDescription = incident.description.substring(0, 160);

  const getStateClassName = (state) => {
    switch (state) {
      case 'Currently in effect':
        return 'state-in-effect';
      case 'Overturned':
        return 'state-overturned';
      case 'Developing':
        return 'state-developing';
      default:
        return 'state-default';
    }
  };

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name='description' content={pageDescription} />
        <meta property='og:title' content={incident.title} />
        <meta property='og:description' content={pageDescription} />
        <meta property='og:type' content='article' />
      </Helmet>

      <Header />

      <main className='detail-page-container'>
        {/* Breadcrumb */}
        <nav className='breadcrumb' aria-label='Breadcrumb'>
          <Link to='/library'>Library</Link>
          <span>/</span>
          <Link to='/incidents'>Incidents</Link>
          <span>/</span>
          <span>{incident.title}</span>
        </nav>

        {/* Title */}
        <h1 className='detail-title'>
          {isAdmin ? (
            <EditableText
              value={editedData.title !== undefined ? editedData.title : incident.title}
              onSave={(val) => handleFieldSave('title', val)}
              tag='span'
            />
          ) : (
            incident.title
          )}
        </h1>

        {/* Two-column layout */}
        <div className='detail-layout'>
          {/* Main column */}
          <div className='detail-main'>
            {/* Description */}
            <section className='detail-description'>
              <p className='incident-description' style={{WebkitLineClamp: 'unset', display: 'block'}}>
                {isAdmin ? (
                  <EditableText
                    value={editedData.description !== undefined ? editedData.description : incident.description}
                    onSave={(val) => handleFieldSave('description', val)}
                    tag='span'
                    multiline={true}
                  />
                ) : (
                  incident.description
                )}
              </p>
            </section>

            {/* Pressure section */}
            {(incident.pressuredBy || incident.pressureDetail) && (
              <section className='detail-section'>
                <h2 className='detail-section-heading'>Pressure</h2>
                {incident.pressureDetail && (
                  <p className='pressure-detail'>
                    {isAdmin ? (
                      <EditableText
                        value={editedData.pressureDetail !== undefined ? editedData.pressureDetail : incident.pressureDetail}
                        onSave={(val) => handleFieldSave('pressureDetail', val)}
                        tag='span'
                        multiline={true}
                      />
                    ) : (
                      incident.pressureDetail
                    )}
                  </p>
                )}
                {incident.pressuredBy && incident.pressuredBy.length > 0 && (
                  <div className='pressure-actors'>
                    <h3 style={{fontSize: '0.9em', fontWeight: 600, marginTop: '1.5rem', marginBottom: '0.5rem', color: '#595959'}}>Actors involved</h3>
                    <ul className='actors-list'>
                      {incident.pressuredBy.map((actorId, idx) => {
                        const org = getOrgById(actorId);
                        return (
                          <li key={idx}>
                            {org ? (
                              <Link to={`/organisations/${org.slug}`}>
                                {org.name}
                              </Link>
                            ) : (
                              <span>Organisation reference</span>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}
              </section>
            )}

            {/* Timeline */}
            {(incident.updates && incident.updates.length > 0) || isAdmin ? (
              <section className='detail-section'>
                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                  <h2 className='detail-section-heading'>Timeline</h2>
                  {isAdmin && (
                    <button
                      className='admin-btn-small'
                      onClick={() => setShowTimelineEditor(true)}
                      title='Edit timeline'
                      style={{padding: '0.25rem 0.5rem', fontSize: '0.85rem'}}
                    >
                      Edit
                    </button>
                  )}
                </div>
                <ol className='timeline-list'>
                  {[...(editedData.updates !== undefined ? editedData.updates : incident.updates || [])]
                    .sort((a, b) => new Date(b.date) - new Date(a.date))
                    .map((event, idx) => (
                      <li key={idx} className='timeline-event'>
                        <span className='timeline-date'>{formatDate(event.date)}</span>
                        <p className='timeline-text'>
                          {event.text}
                          {event.source && (
                            <>
                              {' '}
                              <a href={event.source} target='_blank' rel='noopener noreferrer' className='timeline-source-link'>
                                [source]
                              </a>
                            </>
                          )}
                        </p>
                      </li>
                    ))}
                </ol>
              </section>
            ) : null}
            {showTimelineEditor && (
              <TimelineEditor
                updates={editedData.updates !== undefined ? editedData.updates : incident.updates || []}
                onSave={handleTimelineSave}
                onClose={() => setShowTimelineEditor(false)}
              />
            )}

            {/* Response and pushback */}
            {incident.response && (
              <section className='detail-section'>
                <h2 className='detail-section-heading'>Response and pushback</h2>
                <p className='incident-response'>
                  {isAdmin ? (
                    <EditableText
                      value={editedData.response !== undefined ? editedData.response : incident.response}
                      onSave={(val) => handleFieldSave('response', val)}
                      tag='span'
                      multiline={true}
                    />
                  ) : (
                    incident.response
                  )}
                </p>
              </section>
            )}
          </div>

          {/* Sidebar */}
          <aside className='detail-sidebar'>
            <dl className='detail-meta-list'>
              {incident.currentState && (
                <>
                  <dt>Status</dt>
                  <dd>
                    <span className={`state-badge ${getStateClassName(incident.currentState)}`}>
                      {incident.currentState}
                    </span>
                  </dd>
                </>
              )}

              {incident.date && (
                <>
                  <dt>Date</dt>
                  <dd>{formatDate(incident.date)}</dd>
                </>
              )}

              {incident.country && (
                <>
                  <dt>Country</dt>
                  <dd>{incident.country}</dd>
                </>
              )}

              {incident.institutionType && (
                <>
                  <dt>Institution</dt>
                  <dd>{incident.institutionType}</dd>
                </>
              )}
            </dl>

            {/* Related materials */}
            {incident.relatedMaterials && incident.relatedMaterials.length > 0 && (
              <div className='detail-sidebar-section'>
                <h3 className='detail-sidebar-heading'>Related materials</h3>
                <ul className='detail-sidebar-links'>
                  {incident.relatedMaterials.map((materialId, idx) => (
                    <li key={idx}>
                      <Link to={`/materials/${materialId}`}>
                        View material
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Related organisations */}
            {incident.relatedOrgs && incident.relatedOrgs.length > 0 && (
              <div className='detail-sidebar-section'>
                <h3 className='detail-sidebar-heading'>Organisations</h3>
                <ul className='detail-sidebar-links'>
                  {incident.relatedOrgs.map((orgId, idx) => {
                    const org = getOrgById(orgId);
                    return (
                      <li key={idx}>
                        {org ? (
                          <Link to={`/organisations/${org.slug}`}>
                            {org.name}
                          </Link>
                        ) : (
                          <span>Organisation reference</span>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}

            {/* Sources */}
            {(incident.sources && incident.sources.length > 0) || isAdmin ? (
              <div className='detail-sidebar-section'>
                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                  <h3 className='detail-sidebar-heading'>Sources</h3>
                  {isAdmin && (
                    <button
                      className='admin-btn-small'
                      onClick={() => setShowSourcesEditor(true)}
                      title='Edit sources'
                      style={{padding: '0.25rem 0.5rem', fontSize: '0.85rem'}}
                    >
                      Edit
                    </button>
                  )}
                </div>
                <ul className='detail-sidebar-links'>
                  {(editedData.sources !== undefined ? editedData.sources : incident.sources || []).map((source, idx) => (
                    <li key={idx}>
                      <a href={source.url} target='_blank' rel='noopener noreferrer'>
                        {source.name}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
            {showSourcesEditor && (
              <SourcesEditor
                sources={editedData.sources !== undefined ? editedData.sources : incident.sources || []}
                onSave={handleSourcesSave}
                onClose={() => setShowSourcesEditor(false)}
              />
            )}
          </aside>
        </div>
      </main>

      <Footer />
    </>
  );
}
